import React, { useCallback, useEffect, useState } from "react";

import { AlertColor, MAX_FILES_SIZE, MAX_TXT_CHARACTERS } from "../../../../util/constants";
import { DocumentData } from "../../../../util/interfaces/DocumentData";
import { FileData } from "../../../../util/interfaces/FileData";
import InputDrop from "../../InputDrop/InputDrop";

import StepOneFileItem from "./StepOneFileItem";
import { validateTxtCaracters } from "@/utils/fileUtils";

interface StepOneProps {
  callbackError: (_message: string, _alertColor: AlertColor, _disableNext: boolean) => void;
  callbackSetFiles: (_files: FileData[]) => void;
  currentFile: DocumentData;
  existingFile?: FileData[];
}

const StepOne: React.FC<StepOneProps> = ({
  callbackError,
  callbackSetFiles,
  existingFile,
  currentFile,
}) => {
  const [files, setFiles] = useState<FileData[]>(existingFile ?? []);
  const [validFiletype, setValidFiletype] = useState(false);
  const [differentFilename, setDifferentFilename] = useState(false);
  const mimeToExtension: { [key: string]: string[] } = {
    "application/pdf": [".pdf"],
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
    "application/vnd.ms-powerpoint": [".ppt"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "text/csv": [".csv"],
    "text/plain": [".txt"],
  };
  const getExtensionsFromMimeType = (mimeType: string): string[] => {
    return mimeToExtension[mimeType] || [];
  };

  useEffect(() => {
    if (existingFile === undefined) {
      setDifferentFilename(false);
    } else {
      for (const item of existingFile) {
        item.file.name === currentFile.documentName
          ? setDifferentFilename(false)
          : setDifferentFilename(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: any[]) => {
      if (fileRejections.length > 0) {
        callbackError("Not supported file.", AlertColor.Error, true);
        return false;
      }
      const textFileCharacterCounts = await validateTxtCaracters(acceptedFiles);
      if (textFileCharacterCounts.some((file) => file.characters > MAX_TXT_CHARACTERS)) {
        callbackError(
          `This .txt file is too large to process (over 1 million characters). Please split it into smaller files and try again`,
          AlertColor.Error,
          files.length == 0
        );
        return false;
      }
      const newFile = acceptedFiles[0];

      if (newFile.size > MAX_FILES_SIZE * 1024 * 1024) {
        callbackError(
          `File too large: ${newFile.name}. Maximum size is ${MAX_FILES_SIZE}MB.`,
          AlertColor.Error,
          true
        );
        return false;
      }

      if (newFile.name === currentFile.documentName) {
        setDifferentFilename(false);
      } else {
        callbackError(
          `This file's name does not match, uploading will override the current version but keep the original file's name`,
          AlertColor.Warning,
          false
        );
        setDifferentFilename(true);
      }
      const fileExtensions = getExtensionsFromMimeType(newFile.type);
      const currentFileFormatted = `.${currentFile.fileType}`;

      if (!fileExtensions.includes(currentFileFormatted)) {
        callbackError(
          `Unable to update file version because you've uploaded a different file type.`,
          AlertColor.Error,
          true
        );
        setValidFiletype(true);
        return false;
      }

      setValidFiletype(false);

      setFiles([
        {
          file: newFile,
          fileCategory: currentFile.category,
          metadata: currentFile.metadata,
          override: true,
        },
      ]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [files]
  );

  useEffect(() => {
    callbackSetFiles(files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  return (
    <div style={{ height: "100%" }} className="grid-override">
      <div style={{ gridArea: "drop-input" }}>
        <InputDrop callbackDrop={onDrop} maxSizeInMB={MAX_FILES_SIZE} height="100%" />
      </div>
      {files.length > 0 && (
        <div className="fileList_container_override">
          <hr />
          <h3 style={{ margin: "2% 0 0 0" }}>Selected file</h3>
          {files.map((file) => (
            <StepOneFileItem
              key={file.file.name}
              file={file}
              differentFilename={differentFilename}
              validFiletype={validFiletype}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default StepOne;
